import { reportPlatformError } from "$root/api/error-reporting";
import { DataDisplayOverlay } from "$root/components/DataDisplayOverlay";
import { typedUrlForRoute } from "$root/components/PlatformRouter/RoutesDef";
import { ReactQueryWrapperBase } from "$root/components/ReactQueryWrapper";
import { investmentCustomAttributesRepository } from "$root/functional-areas/customAttributes/repository";
import { useDebouncedNameUniquenessChecker } from "$root/functional-areas/named-entities/uniqueness";
import { portfolioRepository } from "$root/functional-areas/portfolio/repository";
import { createPrimaryBenchmarkLabel, unifyReferenceAndCustomBenchmark } from "$root/functional-areas/reference/common";
import { useUserValue } from "$root/functional-areas/user";
import { userRepository } from "$root/functional-areas/user/repository";
import { ManualCreationEntitiesEnum } from "$root/pages/ManualCreation/common";
import { generateIntentId, trackMixPanelEvent } from "$root/third-party-integrations/initMixPanel";
import { FormController } from "$root/third-party-integrations/react-hook-form";
import { useMappedQuery } from "$root/third-party-integrations/react-query/hooks";
import { FormFields } from "$root/ui-lib/form/FormFields";
import { valueByPath } from "$root/utils";
import { InfoTooltip } from "$root/widgets-architecture/layout/WidgetsMapper/InfoTooltip";
import { FormField, Label, Radio, RadioGroup } from "@mdotm/shadcn/mdotm/components";
import {
	type Color,
	type Currencies,
	type CustomAttributeValue,
	type InvestmentBenchmarkDTO,
	type InvestmentCreationConfigurationControllerV4Api,
	type InvestmentDraftConfigurationControllerV4Api,
	type InvestmentEnhancementConfigurationControllerV4Api,
	type InvestmentReferenceDTO,
	type TargetVolatility,
	MainInfoSaveRequestMandateTypeEnum,
} from "@eu.mdotm.api/sphere";
import { zodResolver } from "@hookform/resolvers/zod";
import {
	type Option,
	ActionText,
	Banner,
	CircularProgressBar,
	Column,
	Icon,
	Text,
	TooltipContent,
} from "@mdotm/shadcn/legacy/components";
import { ForEach } from "@mdotm/utils/react-extra";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import type { EditPortfolioV4Props } from "../EditPortfolio.shared-types";
import { StepBase } from "../StepBase";
import { assocStatusToEntity } from "../constraints/const";
import { useStepSync } from "../hooks";
import { makeStepToRequestMapper, responseToStepMapper } from "../requests";
import {
	useHandleSubmitToCustomSubmitHandlerInContext,
	type EditPortfolioV4StepPayloadMap,
	type StepPropsFor,
} from "../shared";
import { useLocaleFormatters } from "$root/localization/hooks";
import { tagColorMap } from "$root/ui-lib/table/colors";
import { zExt } from "$root/third-party-integrations/zod";
import { PortfolioCustomAttribute } from "$root/functional-areas/reports/components/PortfolioCustomAttribute";
import { isAumSystemAttribute } from "$root/functional-areas/customAttributes/aum";
import { AumField } from "$root/functional-areas/customAttributes/AumField";
import { refineAumCap } from "$root/functional-areas/customAttributes/aumCap";
import { refineCustomAttributeDecimals } from "$root/functional-areas/customAttributes/decimalCap";
import type { CustomAttributeDto } from "$root/functional-areas/customAttributes/type";
import { canSendToExecution } from "$root/functional-areas/portfolio/execution";
import { canCreateInQuantities } from "$root/functional-areas/instruments-editor/quantity/gate";
import { QuantityModeField } from "$root/functional-areas/instruments-editor/quantity/creation/QuantityModeField";
import { refineQuantityModeAum } from "$root/functional-areas/instruments-editor/quantity/creation/aumRequired";

export function getMainInfoStepData(
	createApi: Omit<InvestmentCreationConfigurationControllerV4Api, "basePath" | "axios">,
	enhanceApi: Omit<InvestmentEnhancementConfigurationControllerV4Api, "basePath" | "axios">,
	draftApi: Omit<InvestmentDraftConfigurationControllerV4Api, "basePath" | "axios">,
	area: EditPortfolioV4Props["area"],
): Promise<EditPortfolioV4StepPayloadMap["mainInfo"]> {
	return (
		!area.portfolioUid
			? createApi.getCreationConfigurationMainInfo()
			: area.name === "draft"
				? draftApi.getDraftConfigurationMainInfo(area.portfolioUid)
				: enhanceApi.getEnhancementConfigurationMainInfo(area.portfolioUid)
	).then(({ data }) => responseToStepMapper.mainInfo(data, area));
}

const MAX_TARGET_VOLATILITY = 50;
const MIN_TARGET_VOLATILITY = 0;

export function Step01_MainInfo({
	context,
	stepMetadata,
	stepData,
	onStepDataChange,
	onStepError,
	toggleDirty,
}: StepPropsFor<"mainInfo">): React.JSX.Element {
	const { saveHandlers, area } = context;
	const { t } = useTranslation();
	const { formatNumber } = useLocaleFormatters();
	const user = useUserValue();
	const customerQuery = userRepository.customer.useQuery(user.customerId!);
	const availableAttributesQuery = investmentCustomAttributesRepository.customAttributes.useQuery();
	const edit = area.editable && area.edit;

	const isEditProcessing = useMemo(() => stepMetadata.processing && edit, [edit, stepMetadata.processing]);
	const fieldsEditable = !isEditProcessing && edit && area.name !== "enhance";
	const creationFieldsEditable =
		!isEditProcessing && edit && area.editable && (area.name === "create" || area.name === "draft");

	const { checkingNameUniqueness, checkIfNameIsAvailable } = useDebouncedNameUniquenessChecker({
		isNameAvailableApi: portfolioRepository.isInvestmentNameAvailable,
		currentName: stepData.name,
	});

	const { control, formState, handleSubmit, reset, watch, getValues, setValue } = useForm({
		defaultValues: stepData,
		resolver: zodResolver(
			z
				.object({
					baseCurrency: z.string(),
					name: z
						.string()
						.min(1, "Please provide a valid name")
						.refine((name) => checkIfNameIsAvailable(name), {
							message: "Name not available",
						}),
					primaryBenchmark: z
						.object(
							{
								benchmarkIdentifier: z.string(),
								benchmarkType: z.string(),
								available: z.boolean(),
								volatility: z.number().nullable().optional(),
								status: z.string(),
								benchmarkName: z.string(),
								inceptionDate: z.string().optional(),
								flaggedAsBenchmark: z.boolean().optional(),
							} satisfies Record<keyof InvestmentBenchmarkDTO, unknown>,
							{ invalid_type_error: "Please select a valid benchmark" },
						)
						.required()
						.passthrough(),
					mandateType: z.nativeEnum(MainInfoSaveRequestMandateTypeEnum).optional(),
					investmentReference: z
						.object({
							name: z.string().nullable(),
							type: z.any(),
							identifier: z.string(),
							available: z.boolean().nullable(),
							volatility: z.number().nullable().optional(),
							status: z.string().nullable(),
							flaggedAsBenchmark: z.boolean().optional(),
						} satisfies Record<keyof InvestmentReferenceDTO, unknown>)
						.optional()
						.nullable(),
					investmentUuid: z.string().optional(),
					targetVolatility: z.object({
						selectedValue: z
							.number({
								required_error: "Please insert a valid number",
								invalid_type_error: "Please insert a valid number",
							})
							.min(MIN_TARGET_VOLATILITY, "Please provide a valid number higher or equal 0")
							.max(MAX_TARGET_VOLATILITY, "Please provide a number lower or equal 50")
							.nullable(),
						suggestedMaxValue: z.number().optional(),
						suggestedMinValue: z.number().optional(),
						suggestedValue: z.number().optional(),
						validity: z.any(),
					} satisfies Record<keyof TargetVolatility, unknown>),
					fixedWeights: z.boolean(),
					quantityMode: z.boolean(),
					liveTrading: z.boolean().optional().nullable(),
					customAttributes: z
						.array(
							z
								.object({
									attributeUuid: zExt.maybe(z.string()),
									name: zExt.maybe(z.string()),
									dateValue: zExt.maybe(z.string()),
									numberValue: zExt.maybe(z.number({ invalid_type_error: "Please enter a valid amount" })),
									textValue: zExt.maybe(z.string()),
									selectedTags: zExt.maybe(z.array(zExt.maybe(z.string()))),
								})
								.optional(),
						)
						.optional()
						.superRefine(refineAumCap(availableAttributesQuery.data))
						.superRefine(refineCustomAttributeDecimals),
				} satisfies Record<keyof typeof stepData, unknown>)
				.superRefine(refineQuantityModeAum(availableAttributesQuery.data)),
		),
	});

	// Every available attribute gets an entry in the form, so its field has a path to bind to: merged
	// into each reset, and appended (keeping whatever was typed) when the attributes load or editing
	// unlocks after the last one.
	const availableAttributes = availableAttributesQuery.data;
	useStepSync({
		reset: (data) =>
			reset(
				fieldsEditable && availableAttributes
					? {
							...data,
							customAttributes: withCustomAttributeSlots(
								data.customAttributes,
								availableAttributes,
								getValues("customAttributes"),
							),
						}
					: data,
			),
		toggleDirty,
		isDirty: formState.isDirty,
		stepData,
	});
	useEffect(() => {
		if (!fieldsEditable || !availableAttributes) {
			return;
		}
		const current = getValues("customAttributes");
		const slotted = withCustomAttributeSlots(current, availableAttributes);
		if (slotted.length !== (current?.length ?? 0)) {
			setValue("customAttributes", slotted);
		}
	}, [fieldsEditable, availableAttributes, getValues, setValue]);
	const stepToRequestMapper = useMemo(() => makeStepToRequestMapper(area.portfolioUid), [area.portfolioUid]);

	const selectableRawQuery = portfolioRepository.wizardMainInfo.useQuery(context.area);

	const selectableQuery = useMappedQuery(
		(selectable) => {
			const currencies: Option<Currencies>[] = (selectable.availableCurrencies ?? []).map((currency) => ({
				label: currency ?? "",
				value: currency,
			}));

			const benchmarks: Option<InvestmentBenchmarkDTO>[] = (selectable.availablePrimaryBenchmarks ?? []).map((el) => ({
				label: createPrimaryBenchmarkLabel(el),
				value: el,
				disabled: !el.available,
				group: t(
					`INVESTMENT_REFERENCE_CATEGORIES.${unifyReferenceAndCustomBenchmark(
						el.benchmarkType!,
						el.flaggedAsBenchmark,
					)}`,
				),
			}));

			const references: Option<InvestmentReferenceDTO>[] = (selectable.availableInvestmentReferences ?? []).map(
				(el) => ({
					label: assocStatusToEntity(el.name, el.status),
					value: el,
					group: t(
						`INVESTMENT_REFERENCE_CATEGORIES.${unifyReferenceAndCustomBenchmark(el.type!, el.flaggedAsBenchmark)}`,
					),
					disabled: !el.available,
				}),
			);

			return { currencies, benchmarks, references };
		},
		[t],
		selectableRawQuery,
	);

	const observedPrimaryBenchmark = watch("primaryBenchmark");
	const observedTargetVolatility = watch("targetVolatility");
	const observedInvestmentReference = watch("investmentReference");

	const onSubmitAsync = useHandleSubmitToCustomSubmitHandlerInContext({
		context,
		stepName: "mainInfo",
		handleSubmit,
		baseSubmitFn: async (values) => {
			try {
				onStepDataChange(
					await saveHandlers.mainInfo({
						...values,
						customAttributes: values.customAttributes.filter((v) => !isCustomAttributeValueNull(v)),
					}),
					{
						skipMetadataUpdate: true,
						persist: true,
					},
				);
			} catch (err) {
				onStepError();
				reportPlatformError(err, "ERROR", "portfolio-creation", {
					message: "Save main info step",
					payload: JSON.stringify(values),
				});
				throw err;
			}
		},
	});

	const { data: customer } = customerQuery;
	const executionAvailable = useMemo(() => canSendToExecution(customer), [customer]);

	return (
		<StepBase optional={stepMetadata.optional} title="Main info" onSubmitAsync={() => onSubmitAsync()}>
			<DataDisplayOverlay
				dataProvider={() => ({
					formData: watch(),
					requestBody: stepToRequestMapper.mainInfo(watch()),
				})}
				dataSource="Main Info"
			/>

			{
				//#region Portfolio Name
			}
			{fieldsEditable ? (
				<FormFields.Text
					className="mb-4 max-w-[470px]"
					control={control}
					formState={formState}
					name="name"
					label="Portfolio name"
					placeholder="Name"
					data-qualifier="portfolioWizard/mainInfo/portfolioName"
					rightContent={
						checkingNameUniqueness ? <CircularProgressBar className="w-3" value="indeterminate" /> : undefined
					}
				/>
			) : (
				<FormField label="Portfolio name" className="mb-4 max-w-[470px]">
					{watch("name") || "-"}
				</FormField>
			)}
			{
				//#endregion
			}

			{
				//#region Currency
			}
			{!isEditProcessing && (area.name === "create" || area.name === "draft") && area.editable ? (
				<ReactQueryWrapperBase
					query={selectableQuery}
					loadingFallback={<CircularProgressBar style={{ width: "1rem" }} value="indeterminate" />}
				>
					{({ currencies }) => (
						<FormFields.Select
							data-qualifier="portfolioWizard/mainInfo/currency/options"
							triggerDataAttrs={{
								"data-qualifier": "portfolioWizard/mainInfo/currency",
							}}
							control={control}
							formState={formState}
							name="baseCurrency"
							className="mb-4 max-w-[470px]"
							label="Base currency"
							placeholder={t("PORTFOLIOS.PORTFOLIO_CURRENCY_PLACEHOLDER")}
							style={{ width: "100%" }}
							options={currencies}
							enableSearch
						/>
					)}
				</ReactQueryWrapperBase>
			) : (
				<FormField label="Base currency" className="mb-4 max-w-[470px]">
					{watch("baseCurrency") || "-"}
				</FormField>
			)}
			{
				//#endregion
			}

			<ReactQueryWrapperBase
				query={availableAttributesQuery}
				loadingFallback={<CircularProgressBar style={{ width: "1rem" }} value="indeterminate" />}
				errorFallback={null}
			>
				{(availableAttributes) => (
					<AumField
						availableAttributes={availableAttributes}
						values={watch("customAttributes")}
						control={control}
						formState={formState}
						edit={fieldsEditable}
						currency={watch("baseCurrency")}
						required={watch("quantityMode")}
					/>
				)}
			</ReactQueryWrapperBase>

			{
				//#region Benchmark
			}
			{fieldsEditable ? (
				<ReactQueryWrapperBase
					query={selectableQuery}
					loadingFallback={<CircularProgressBar style={{ width: "1rem" }} value="indeterminate" />}
				>
					{({ benchmarks }) => (
						<>
							<FormFields.Select
								enableSearch
								data-qualifier="portfolioWizard/mainInfo/benchmark/options"
								triggerDataAttrs={{
									"data-qualifier": "portfolioWizard/mainInfo/benchmark",
								}}
								control={control}
								formState={formState}
								name="primaryBenchmark"
								className={{
									"max-w-[470px]": true,
									"mb-2": watch("primaryBenchmark.benchmarkType") === "STANDARD_BENCHMARK",
									"mb-4": watch("primaryBenchmark.benchmarkType") !== "STANDARD_BENCHMARK",
								}}
								options={benchmarks}
								placeholder={t("PORTFOLIOS.PORTFOLIO_BENCHMARK_PLACEHOLDER")}
								style={{ width: "100%" }}
								label={({ htmlFor }) => (
									<div className="flex items-center gap-2 mb-px">
										<Label htmlFor={htmlFor}>Benchmark</Label>
										<InfoTooltip>
											<TooltipContent>
												<Text type="Body/M/Book" as="div">
													Some benchmarks might be unavailable for selection if:
													<ul className="list-disc pl-8">
														<li>
															they are not in <strong>READY</strong> status
														</li>
														<li>
															they have <strong>drifting weights</strong> and the <strong>inception date</strong> is
															after the portfolio&apos;s inception
														</li>
														<li>
															they have <strong>fixed weights</strong>, but one or more instruments{" "}
															<strong>lack historical</strong> data covering the full portfolio timespan
														</li>
													</ul>
												</Text>
											</TooltipContent>
										</InfoTooltip>
									</div>
								)}
							/>
							{watch("primaryBenchmark.benchmarkType") === "STANDARD_BENCHMARK" && (
								<Banner severity="info" className="mb-4 max-w-[470px]">
									To keep this standard benchmark available for comparison,
									<ActionText
										onClick={() => {
											const intentId = generateIntentId();
											trackMixPanelEvent("reference.entityActions.createIntent", { intentId });
											window.open(
												typedUrlForRoute("Portfolios/ManualCreation", {
													entity: ManualCreationEntitiesEnum.Reference,
													copyComposition: "true",
													preselectedTemplate: getValues("primaryBenchmark.benchmarkIdentifier"),
													intentId,
												}),
												"_blank",
											);
										}}
									>
										create a Reference
									</ActionText>{" "}
									starting from its composition.
								</Banner>
							)}
						</>
					)}
				</ReactQueryWrapperBase>
			) : (
				<FormField label="Benchmark" className="mb-4 max-w-[470px]">
					{observedPrimaryBenchmark?.benchmarkName}
				</FormField>
			)}

			{
				//#endregion
			}

			{
				//#region Reference
			}
			{!isEditProcessing && edit && area.editable ? (
				<ReactQueryWrapperBase
					query={selectableQuery}
					loadingFallback={<CircularProgressBar style={{ width: "1rem" }} value="indeterminate" />}
				>
					{({ references }) => (
						<div className="relative mb-4 max-w-[470px]">
							<FormFields.Select
								enableSearch
								data-qualifier="portfolioWizard/mainInfo/reference/options"
								triggerDataAttrs={{
									"data-qualifier": "portfolioWizard/mainInfo/reference",
								}}
								control={control}
								formState={formState}
								name="investmentReference"
								label={({ htmlFor }) => (
									<div className="flex items-center w-fit mb-px space-x-2">
										<Label htmlFor={htmlFor}>Reference</Label>{" "}
										<InfoTooltip align="startToEnd">
											<TooltipContent>
												<Text type="Body/M/Book">
													The reference can be used to represent either the neutral positioning of your portfolio or to
													apply a Tracking Error constraint in the Risk Constraints section below.
												</Text>
											</TooltipContent>
										</InfoTooltip>
									</div>
								)}
								placeholder={t("PORTFOLIOS.PORTFOLIO_REFERENCE_PLACEHOLDER")}
								style={{ width: "100%" }}
								options={references}
								unselectOnMatch
							/>

							<div className="absolute right-0 top-0">
								<Text type="Body/M/Book" as="p" className="text-right">
									Optional
								</Text>
							</div>
						</div>
					)}
				</ReactQueryWrapperBase>
			) : (
				<FormField label="Reference" className="mb-4 max-w-[470px]">
					{observedInvestmentReference?.name || "-"}
				</FormField>
			)}
			{
				//#endregion
			}

			{
				//#region Target Volatility
			}
			<ReactQueryWrapperBase
				query={selectableQuery}
				loadingFallback={<CircularProgressBar style={{ width: "1rem" }} value="indeterminate" />}
			>
				{() => {
					const benchmarkVol = observedPrimaryBenchmark?.volatility;
					const referenceVol = observedInvestmentReference?.volatility;

					return (
						<div className="max-w-[470px] overflow-hidden mb-4">
							{!isEditProcessing && edit && area.editable ? (
								<div>
									<div className="flex space-x-2 items-end w-full mb-2">
										<FormFields.NullableNumber
											control={control}
											data-qualifier="portfolioWizard/mainInfo/targetVolatility"
											name="targetVolatility.selectedValue"
											label={({ htmlFor }) => (
												<Label htmlFor={htmlFor} className="mb-px">
													Target volatility
												</Label>
											)}
											formState={formState}
											className="max-w-[110px]"
											inputAppearance={{ style: { textAlign: "right" } }}
											rightContent={<Icon icon="Percentile" size={14} />}
											hideBanner
										/>
										<div className="flex-1 flex flex-col justify-end w-[calc(470px-110px-8px)] relative h-[51px]">
											<div className="absolute right-0 top-0">
												<Text type="Body/M/Book" as="p" className="text-right">
													Optional
												</Text>
											</div>

											<Text type="Body/M/Book" as="div" className="truncate pr-4 h-fit">
												{benchmarkVol ? `Benchmark volatility: ${formatNumber(benchmarkVol, 2)}%` : ""}
											</Text>
											<Text type="Body/M/Book" as="div" className="truncate pr-4 h-fit leading-none!">
												{referenceVol ? `Reference volatility: ${formatNumber(referenceVol, 2)}%` : ""}
											</Text>
										</div>
									</div>
									{formState.errors.targetVolatility?.selectedValue?.message && (
										<Banner severity="error">{formState.errors.targetVolatility?.selectedValue?.message}</Banner>
									)}
								</div>
							) : (
								<FormField label="Target Volatility" className="max-w-[470px]">
									<Text as="p" type="Body/M/Book">
										Value:{"\xa0"}
										<Text as="span" type="Body/S/Bold">
											{observedTargetVolatility?.selectedValue ? `${observedTargetVolatility?.selectedValue}%` : "-"}
										</Text>
									</Text>
									{benchmarkVol || referenceVol ? (
										<>
											(
											{benchmarkVol
												? `Benchmark volatility: ${formatNumber(benchmarkVol, 2)}%${referenceVol ? "\xa0" : ""}`
												: ""}
											{referenceVol ? `Reference volatility: ${formatNumber(referenceVol, 2)}%` : ""})
										</>
									) : (
										""
									)}
								</FormField>
							)}
						</div>
					);
				}}
			</ReactQueryWrapperBase>
			{
				//#endregion
			}

			<QuantityModeField
				control={control}
				formState={formState}
				setFixedWeights={(fixedWeights) => setValue("fixedWeights", fixedWeights, { shouldDirty: true })}
				edit={creationFieldsEditable}
				entitled={canCreateInQuantities(user)}
			/>

			<Column flexGrow={0} gap={16}>
				{
					//#region Fixed weights
				}
				<FormField
					label={({ htmlFor }) => (
						<div className="flex items-center gap-2 mb-px">
							<Label htmlFor={htmlFor}>Weighting method</Label>
							<InfoTooltip align="startToEnd">
								<TooltipContent>
									<Text type="Body/M/Book">
										<strong>Drifting</strong> weights adjust daily based on market value changes over time, while{" "}
										<strong>Fixed</strong> weights remain unchanged until the next rebalancing.
									</Text>
								</TooltipContent>
							</InfoTooltip>
						</div>
					)}
					error={(valueByPath(formState.errors, "fixedWeights") as { message?: string })?.message}
				>
					{creationFieldsEditable
						? (fieldProps) => (
								<FormController
									control={control}
									name="fixedWeights"
									render={({ field: controllerProps }) => (
										<RadioGroup {...controllerProps} {...fieldProps} onChange={controllerProps.onChange}>
											<div className="flex flex-row flex-wrap gap-4">
												<Radio value={false} data-qualifier="portfolioWizard/mainInfo/weightBehavior(Drifting)">
													Drifting
												</Radio>

												<Radio
													value
													disabled={watch("quantityMode")}
													data-qualifier="portfolioWizard/mainInfo/weightBehavior(Fixed)"
												>
													Fixed
												</Radio>
											</div>
										</RadioGroup>
									)}
								/>
							)
						: watch("fixedWeights")
							? "Fixed"
							: "Drifting"}
				</FormField>
				{
					//#endregion
				}

				{
					//#region Live/Paper trading (Portfolio Execution)
				}
				<ReactQueryWrapperBase query={customerQuery}>
					{(_customer) => (
						<FormField
							label={({ htmlFor }) => (
								<div className="flex items-center gap-2 mb-px">
									<Label htmlFor={htmlFor}>Portfolio type</Label>
									<InfoTooltip align="startToEnd">
										<TooltipContent className="whitespace-pre-line">
											<Text type="Body/M/Bold">Paper Trading</Text>
											<Text type="Body/M/Book">
												{" "}
												keeps the portfolio for internal use only, without enabling execution.{"\n"}
											</Text>
											<Text type="Body/M/Bold">Live Trading</Text>
											<Text type="Body/M/Book">
												{" "}
												makes the portfolio available for execution and allows sending it to external systems for
												trading.
											</Text>
										</TooltipContent>
									</InfoTooltip>
								</div>
							)}
							error={(valueByPath(formState.errors, "portfolioType") as { message?: string })?.message}
						>
							{!isEditProcessing &&
							edit &&
							area.editable &&
							(area.name === "create" || area.name === "draft" || area.name === "settings-current")
								? (fieldProps) => (
										<FormController
											control={control}
											name="liveTrading"
											render={({ field: controllerProps }) => (
												<RadioGroup
													{...controllerProps}
													{...fieldProps}
													value={controllerProps.value ?? null}
													onChange={controllerProps.onChange}
													disabled={!executionAvailable}
												>
													<div className="flex flex-row flex-wrap gap-4">
														<Radio value={false} data-qualifier="portfolioWizard/mainInfo/liveTrading(paper)">
															Paper trading
														</Radio>
														<Radio value data-qualifier="portfolioWizard/mainInfo/liveTrading(live)">
															Live trading
														</Radio>
													</div>
												</RadioGroup>
											)}
										/>
									)
								: watch("liveTrading")
									? t(`PORTFOLIOS.PORTFOLIO_TYPE_LIVE`)
									: t(`PORTFOLIOS.PORTFOLIO_TYPE_PAPER`)}
						</FormField>
					)}
				</ReactQueryWrapperBase>
				{
					//#endregion
				}

				{
					//#region Custom attributes
				}
				<div className="mb-4">
					<ReactQueryWrapperBase query={availableAttributesQuery}>
						{(availableAttributes) => {
							// AUM renders on its own, as a portfolio attribute (`AumField`).
							const userAttributes = availableAttributes.filter((attribute) => !isAumSystemAttribute(attribute));
							return (
								userAttributes.length > 0 && (
									<>
										<Text as="div" className="mb-2" type="Body/L/Bold">
											Custom Attributes
										</Text>
										<div className="space-y-3">
											{fieldsEditable ? (
												<ForEach collection={watch("customAttributes")}>
													{(attribute, index) => {
														// <FormFields.Select/>
														const matchingCustomAttribute = userAttributes.find(
															(customAttribute) => customAttribute.attributeUuid === attribute.attributeUuid,
														);

														if (!matchingCustomAttribute) {
															return "";
														}
														const name = matchingCustomAttribute.name ?? "Nameless Attribute";
														switch (matchingCustomAttribute.attributeType) {
															case "DATE":
																return (
																	<FormFields.Date
																		enableClear
																		data-qualifier={`portfolioWizard/mainInfo/customAttribute/${name}`}
																		control={control}
																		formState={formState}
																		name={`customAttributes.${index}.dateValue`}
																		label={name}
																		className="max-w-[470px]"
																	/>
																);
															case "TAG": {
																const matchingAttribute = availableAttributes.find(
																	(availableAttr) => availableAttr.attributeUuid === attribute.attributeUuid,
																);

																if (!matchingAttribute) {
																	return;
																}

																const tagOptions = (matchingAttribute.tags ?? []).flatMap((tag) =>
																	!tag.value
																		? []
																		: [
																				{
																					label: tag.value,
																					value: tag.value,
																					color: tagColorMap[(tag.color ?? "GRAY") as Color]?.badgeColor(),
																				},
																			],
																);

																return (
																	<FormFields.TagSelect
																		data-qualifier={`portfolioWizard/mainInfo/customAttribute/${name}`}
																		control={control}
																		formState={formState}
																		name={
																			matchingAttribute.multiTag
																				? `customAttributes.${index}.selectedTags`
																				: `customAttributes.${index}.selectedTags.0`
																		}
																		label={name}
																		className="max-w-[470px]"
																		multi={matchingAttribute.multiTag ?? false}
																		options={tagOptions}
																	/>
																);
															}
															case "NUMBER":
																return (
																	<FormFields.NullableNumber
																		data-qualifier={`portfolioWizard/mainInfo/customAttribute/${name}`}
																		control={control}
																		formState={formState}
																		name={`customAttributes.${index}.numberValue`}
																		label={name}
																		className="max-w-[470px]"
																	/>
																);
															case "TEXT":
																return (
																	<FormFields.Text
																		data-qualifier={`portfolioWizard/mainInfo/customAttribute/${name}`}
																		control={control}
																		formState={formState}
																		name={`customAttributes.${index}.textValue`}
																		label={name}
																		className="max-w-[470px]"
																	/>
																);
														}
													}}
												</ForEach>
											) : (
												<ForEach collection={userAttributes}>
													{(attribute) => {
														const customAttributeRelativeToInvestment = getValues("customAttributes").find(
															(attr) => attr.attributeUuid === attribute.attributeUuid,
														);

														return (
															<FormField label={attribute.name} className="max-w-[470px]">
																<PortfolioCustomAttribute
																	availableAttributes={availableAttributes}
																	customAttributeRelativeToInvestment={customAttributeRelativeToInvestment}
																/>
															</FormField>
														);
													}}
												</ForEach>
											)}
										</div>
									</>
								)
							);
						}}
					</ReactQueryWrapperBase>
				</div>
				{
					//#endregion
				}
			</Column>
		</StepBase>
	);
}
/**
 * `values` plus an empty entry for each available attribute it has none for: the one `held` still keeps
 * empty when there is one, since it carries the key its field registered and a fresh one would read as dirty.
 */
function withCustomAttributeSlots(
	values: CustomAttributeValue[] | undefined,
	availableAttributes: CustomAttributeDto[],
	held?: CustomAttributeValue[],
): CustomAttributeValue[] {
	const current = values ?? [];
	return current.concat(
		availableAttributes
			.filter((available) => !current.some((value) => value.attributeUuid === available.attributeUuid))
			.map((available) => {
				const heldEmpty = held?.find(
					(entry) => entry.attributeUuid === available.attributeUuid && isCustomAttributeValueNull(entry),
				);
				if (heldEmpty) {
					return heldEmpty;
				}
				const base = { attributeUuid: available.attributeUuid, name: available.name };
				return available.multiTag ? { ...base, selectedTags: [] } : base;
			}),
	);
}

function isCustomAttributeValueNull(customAttribute: CustomAttributeValue) {
	const isCustomTagsEmpty = customAttribute.selectedTags ? customAttribute.selectedTags.every((x) => x == null) : true;
	return (
		customAttribute.dateValue == null &&
		customAttribute.numberValue == null &&
		customAttribute.textValue == null &&
		isCustomTagsEmpty
	);
}
